import type { CarouselOption } from "Components/Shared/Inputs/CarouselPicker/types";
import { type RefObject, useState } from "react";
import type { PointerEvent, TouchEvent } from "react";
import { GAP_WIDTH } from "../constants";

type UseCarouselManagerProps<T> = {
  options: Readonly<Array<CarouselOption<T>>>;
  value: T;
  isInfinite?: boolean;
  nextSlideThreshold: number;
  onSelect?: (option: CarouselOption<T>) => void;
  carouselRef: RefObject<HTMLDivElement | null>;
};

type CarouselManagerReturn = {
  currentIndex: number;
  dragDistance: number;
  hasDragged: boolean;
  trackEventHandlers: {
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
    onTouchStart: (e: TouchEvent) => void;
    onTouchMove: (e: TouchEvent) => void;
    onTouchEnd: () => void;
  };
  updateCurrentIndex: (index: number) => void;
};

export const useCarouselManager = <T extends string>({
  options,
  value,
  isInfinite = false,
  nextSlideThreshold,
  onSelect,
  carouselRef,
}: UseCarouselManagerProps<T>): CarouselManagerReturn => {
  // Controlled: the track position is always derived from the `value` prop;
  // selection is only reported via onSelect and comes back through props.
  const selectedIndex = options.findIndex((option) => option.value === value);
  const currentIndex = selectedIndex < 0 ? 0 : selectedIndex;

  const [isDragging, setIsDragging] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  // Drag handlers write inline styles for a smooth live drag; clearing them
  // hands the position back to the prop-driven transform on CarouselTrack
  // (inline styles would otherwise override it on external value changes).
  const resetTrackPosition = () => {
    if (carouselRef.current) {
      carouselRef.current.style.transition = "";
      carouselRef.current.style.transform = "";
    }
  };

  const updateCurrentIndex = (nextIndex: number) => {
    resetTrackPosition();
    onSelect?.(options[nextIndex]);
  };

  const handleNext = () => {
    const isLastSlide = currentIndex === options.length - 1;
    updateCurrentIndex(
      isLastSlide ? (isInfinite ? 0 : currentIndex) : currentIndex + 1,
    );
  };

  const handlePrev = () => {
    const isFirstSlide = currentIndex === 0;
    updateCurrentIndex(
      isFirstSlide
        ? isInfinite
          ? options.length - 1
          : currentIndex
        : currentIndex - 1,
    );
  };

  // Drag handlers
  const handleDragStart = ({ clientX }: PointerEvent) => {
    setIsDragging(true);
    setDragStartX(clientX);
    setHasDragged(false);
  };

  const handleDragMove = ({ clientX }: PointerEvent) => {
    if (!isDragging || isSwiping) return;

    setDragDistance(clientX - dragStartX);

    if (carouselRef.current) {
      carouselRef.current.style.transition = "none";
      carouselRef.current.style.transform = `translateX(calc(-${
        currentIndex * 100
      }% + ${dragDistance - GAP_WIDTH * currentIndex}px))`;
    }

    if (Math.abs(clientX - dragStartX) > 5) {
      setHasDragged(true);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || isSwiping) return;
    setIsDragging(false);

    if (dragDistance > nextSlideThreshold) {
      handlePrev();
    } else if (dragDistance < -nextSlideThreshold) {
      handleNext();
    } else {
      resetTrackPosition();
    }

    setDragDistance(0);
  };

  // Touch handlers
  const handleTouchStart = ({ touches }: TouchEvent) => {
    setIsSwiping(true);
    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = "hidden";
    setDragStartX(touches[0].clientX);
  };

  const handleTouchMove = ({ touches }: TouchEvent) => {
    if (!isSwiping) return;

    const currentX = touches[0].clientX;
    setDragDistance(currentX - dragStartX);

    if (carouselRef.current) {
      carouselRef.current.style.transition = "none";
      carouselRef.current.style.transform = `translateX(calc(-${
        currentIndex * 100
      }% + ${dragDistance - GAP_WIDTH * currentIndex}px))`;
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    document.body.style.overflowX = "";
    document.body.style.overflowY = "";
    setIsSwiping(false);

    if (dragDistance > nextSlideThreshold) {
      handlePrev();
    } else if (dragDistance < -nextSlideThreshold) {
      handleNext();
    } else {
      resetTrackPosition();
    }

    setDragDistance(0);
  };

  return {
    currentIndex,
    dragDistance,
    hasDragged,
    trackEventHandlers: {
      onPointerDown: handleDragStart,
      onPointerMove: handleDragMove,
      onPointerUp: handleDragEnd,
      onPointerLeave: handleDragEnd,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    updateCurrentIndex,
  };
};
